import { call, fork, put, SagaReturnType, select, take } from "redux-saga/effects";

import backOfficeRatesApi from "../../api/apiServices/backOffice/controllers/rates/rates.api";
import backOfficeWishElementApi from "../../api/apiServices/backOffice/controllers/wishElement/wishElement.api";
import { RateDataConverter } from "../../viewModels/Rate/RateModel";
import slice from "./reducer";
import {
  makeSelectActiveTab,
  makeSelectCalculationVehicleId,
  makeSelectItems,
  makeSelectOpen,
  makeSelectPage,
  makeSelectPageSize,
  makeSelectRateId,
  makeSelectTotal,
} from "./selectors";
import { RateHistoryTab } from "./types";

function getFilterByOptions(tab: RateHistoryTab) {
  return tab === "SELECTED_SERVICES";
}

function* loadHistoryWorker(page: number, append: boolean) {
  const open: ReturnType<typeof makeSelectOpen> = yield select(makeSelectOpen);
  const rateId: ReturnType<typeof makeSelectRateId> = yield select(makeSelectRateId);
  const pageSize: ReturnType<typeof makeSelectPageSize> = yield select(makeSelectPageSize);
  const activeTab: ReturnType<typeof makeSelectActiveTab> = yield select(makeSelectActiveTab);

  if (!open || !rateId) {
    yield put(slice.actions.loadHistoryReject());
    return;
  }

  const { success, reject }: SagaReturnType<typeof backOfficeRatesApi.getHistory> = yield call(backOfficeRatesApi.getHistory, {
    rateId,
    page,
    size: pageSize,
    filterByOptions: getFilterByOptions(activeTab),
  });

  const currentOpen: ReturnType<typeof makeSelectOpen> = yield select(makeSelectOpen);
  const currentRateId: ReturnType<typeof makeSelectRateId> = yield select(makeSelectRateId);
  const currentTab: ReturnType<typeof makeSelectActiveTab> = yield select(makeSelectActiveTab);

  if (!currentOpen || currentRateId !== rateId || currentTab !== activeTab) {
    return;
  }

  if (success) {
    yield put(
      slice.actions.loadHistorySuccess({
        items: RateDataConverter.convertRates(success.payload.data),
        total: success.payload.total,
        page,
        append,
      }),
    );
  }

  if (reject) {
    yield put(slice.actions.loadHistoryReject());
  }
}

function* openModalWatcher() {
  while (true) {
    yield take(slice.actions.openModal.type);
    yield call(loadHistoryWorker, 1, false);
  }
}

function* changeTabWatcher() {
  while (true) {
    yield take(slice.actions.changeTab.type);
    yield call(loadHistoryWorker, 1, false);
  }
}

function* loadMoreWatcher() {
  while (true) {
    yield take(slice.actions.loadMore.type);

    const items: ReturnType<typeof makeSelectItems> = yield select(makeSelectItems);
    const total: ReturnType<typeof makeSelectTotal> = yield select(makeSelectTotal);
    const page: ReturnType<typeof makeSelectPage> = yield select(makeSelectPage);

    if (items.length < total) {
      yield call(loadHistoryWorker, page + 1, true);
    }
  }
}

function* retryLoadWatcher() {
  while (true) {
    yield take(slice.actions.retryLoad.type);

    const items: ReturnType<typeof makeSelectItems> = yield select(makeSelectItems);
    const total: ReturnType<typeof makeSelectTotal> = yield select(makeSelectTotal);
    const page: ReturnType<typeof makeSelectPage> = yield select(makeSelectPage);
    const append = items.length > 0 && items.length < total;

    yield call(loadHistoryWorker, append ? page + 1 : 1, append);
  }
}

function* createWishElementByRateWatcher() {
  while (true) {
    const { payload }: ReturnType<typeof slice.actions.createWishElementByRate> = yield take(slice.actions.createWishElementByRate.type);
    const calculationVehicleId: ReturnType<typeof makeSelectCalculationVehicleId> = yield select(makeSelectCalculationVehicleId);

    if (!calculationVehicleId) {
      yield put(slice.actions.createWishElementByRateReject());
      continue;
    }

    const { success, reject }: SagaReturnType<typeof backOfficeWishElementApi.createByRate> = yield call(
      backOfficeWishElementApi.createByRate,
      {
        rateId: payload.rateId,
        calculationVehicleId,
        fromHistory: true,
      },
    );

    if (success) {
      yield put(slice.actions.createWishElementByRateSuccess());
    }

    if (reject) {
      yield put(slice.actions.createWishElementByRateReject());
    }
  }
}

export default function* rateHistoryModalSaga() {
  yield fork(openModalWatcher);
  yield fork(changeTabWatcher);
  yield fork(loadMoreWatcher);
  yield fork(retryLoadWatcher);
  yield fork(createWishElementByRateWatcher);
}
